import type { ParseKeys } from 'i18next'
import type { LucideIcon } from 'lucide-react'

import type { Permission } from '@/shared/auth/permissions'

/**
 * Item do menu lateral. Cada módulo exporta os seus em `src/modules/<modulo>/navigation.ts`
 * e eles são agregados em `src/app/navigation.ts`.
 */
export interface NavItem {
  to: string
  labelKey: ParseKeys
  icon: LucideIcon
  /** Permissão necessária para ver o item (ex.: "users.view"). */
  permission?: Permission
  /** Seção do menu em que o item aparece; itens consecutivos com a mesma seção ficam juntos. */
  group?: ParseKeys
  /** Ativo apenas na rota exata (útil para "/"). */
  exact?: boolean
  /**
   * Subitens: o item vira um grupo recolhível e `to` serve só para saber se ele
   * está ativo. Subitens não têm seção nem subitens próprios.
   */
  children?: NavItem[]
}

/** Remove itens sem permissão; um grupo sem subitens visíveis some do menu. */
export function visibleNavItems(items: NavItem[], allowed: (item: NavItem) => boolean): NavItem[] {
  return items.flatMap((item) => {
    if (!allowed(item)) return []
    if (!item.children) return [item]

    const children = item.children.filter(allowed)
    return children.length > 0 ? [{ ...item, children }] : []
  })
}

/** Lista plana com os itens que levam a uma página (subitens no lugar do grupo). */
export function flattenNavItems(items: NavItem[]): NavItem[] {
  return items.flatMap((item) =>
    item.children ? item.children.map((child) => ({ ...child, group: item.group })) : [item],
  )
}
